
import { buildRestApiUrl, getStandardHeaders, buildFunctionUrl } from '@/integrations/supabase/urlBuilder';
import { Rental } from '@/services/types/rentalTypes';
import { toast } from '@/components/ui/sonner';

// Получение активных аренд курьера с улучшенным форматированием запроса и обработкой данных
export const fetchCourierActiveRentals = async (
  courierId: string,
  signal?: AbortSignal
): Promise<Rental[]> => {
  try {
    console.log('Загружаем активные и ожидающие аренды для курьера:', courierId);
    
    if (!courierId || courierId.trim() === '') {
      console.error('Ошибка: ID курьера пустой или неопределенный');
      return [];
    }
    
    // Исправленный запрос с дополнением статуса "pending" для отображения всех активных аренд
    const endpoint = `rentals?` + 
      `courier_id=eq.${encodeURIComponent(courierId)}` +
      `&or=(status.eq.active,status.eq.pending)` +
      `&select=id,bike_id,start_date,end_date,status,amount,human_number,subscription_id,` +
      `bikes:bike_id(id,model,serial_number),` +
      `subscriptions:subscription_id(id,payment_method_id,status)` +
      `&order=start_date.desc`;
    
    const url = buildRestApiUrl(endpoint);
    console.log('URL запроса аренд:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...getStandardHeaders(),
        'credentials': 'include' // Добавляем передачу куки для авторизации
      },
      signal
    });
    
    if (!response.ok) {
      console.error('Ошибка HTTP при загрузке аренд:', response.status, response.statusText);
      
      // Дополнительная проверка на ошибки авторизации
      if (response.status === 401 || response.status === 403) {
        toast.error('Ошибка авторизации при загрузке аренд', {
          description: 'Проверьте права доступа или перезайдите в систему'
        });
      }
      
      throw new Error(`Ошибка при загрузке аренд курьера: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`Загружено ${data.length} активных и ожидающих аренд для курьера ${courierId}:`, data);
    
    if (!Array.isArray(data)) {
      console.error('Ошибка: Полученные данные не являются массивом:', data);
      return [];
    }
    
    // вся логика нормализации данных остается без изменений
    const normalizedData = data.map(rental => {
      console.log(`Обработка аренды ${rental.id}:`, {
        'bike_id': rental.bike_id,
        'bikeData': rental.bikes,
        'subscription_id': rental.subscription_id,
        'subscriptionData': rental.subscriptions,
      });
      
      const bikeData = rental.bikes || null;
      const subscriptionData = rental.subscriptions || null;
      
      const hasActiveSubscription = subscriptionData && 
                                  subscriptionData.status === 'active' && 
                                  subscriptionData.payment_method_id != null;
      
      if (hasActiveSubscription) {
        console.log(`Аренда ${rental.id} имеет АКТИВНУЮ подписку с платежными данными:`, 
          subscriptionData);
      } else if (subscriptionData) {
        console.log(`Аренда ${rental.id} имеет подписку, но без активных платежных данных:`, 
          subscriptionData);
      }
      
      return {
        ...rental,
        bikeId: rental.bike_id,
        startDate: rental.start_date,
        endDate: rental.end_date,
        bikes: bikeData || { 
          id: rental.bike_id, 
          model: 'Неизвестная модель', 
          serial_number: 'Н/Д' 
        },
        subscriptionInfo: subscriptionData 
          ? {
              id: subscriptionData.id,
              status: subscriptionData.status,
              payment_method_id: subscriptionData.payment_method_id
            }
          : null,
        hasActiveSubscription
      };
    });
    
    console.log('Нормализованные данные аренд:', normalizedData);
    return normalizedData;
  } catch (error: any) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('Запрос аренд был отменен');
      throw error;
    }
    
    console.error('Ошибка при получении активных аренд курьера:', error);
    toast.error('Не удалось загрузить активные аренды', {
      description: error.message
    });
    return [];
  }
};

// Интерфейс для результата функции взыскания
interface CollectionResult {
  success: boolean;
  status?: string;
  message?: string;
  transactionId?: string;
  data?: any;
  error?: string;
}

// Функция для отправки запроса на сбор с правильными параметрами
export const submitCollectionRequest = async (
  courierId: string,
  paymentMethodId: string,
  amount: number,
  reason: string,
  couriers: any[],
  paymentMethods: any[],
  isAuthRequest: boolean
): Promise<CollectionResult> => {
  try {
    console.log('Отправка запроса на сбор:', {
      courierId,
      paymentMethodId,
      amount,
      reason,
      isAuthRequest
    });
    
    const requestData = {
      courierId,
      paymentMethodId,
      amount,
      reason,
      couriers,
      paymentMethods,
      isAuthRequest
    };
    
    const url = buildFunctionUrl('collection-request');
    
    const response = await fetch(url, {
      method: 'POST',
      headers: getStandardHeaders(),
      body: JSON.stringify(requestData)
    });
    
    if (!response.ok) {
      throw new Error(`Ошибка при отправке запроса: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('Результат отправки запроса на сбор:', result);
    
    return { 
      success: true, 
      status: result.status || 'success',
      message: result.message || 'Запрос успешно отправлен',
      transactionId: result.transactionId,
      data: result 
    };
  } catch (error: any) {
    console.error('Ошибка при отправке запроса на сбор:', error);
    return { 
      success: false, 
      message: error.message || 'Неизвестная ошибка',
      error: error.message 
    };
  }
};
